import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const ADMIN_BASE_URL = "/api/Admin/v1.0";

const useAdmin = () => {
  const { api, AdminId } = useGlobalContext();

  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolePermissionsLoading, setRolePermissionsLoading] = useState(false);
  const [createRoleLoading, setCreateRoleLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [assignRoleLoading, setAssignRoleLoading] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  const [sideBarUpdateLoading, setSideBarUpdateLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getArrayFromResponse = (data) => {
    if (Array.isArray(data?.response)) return data.response;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const resolveUserId = (userId = "") => String(userId || AdminId || "");

  // ================= GET PERMISSION KEYS =================
  const FetchPermissionKeys = async (
    userId = "",
    setPermissionKeys,
    setFilteredPermissionKeys
  ) => {
    setPermissionsLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/getPermissionKeys`, {
        userId: resolveUserId(userId),
      });

      if (response.data?.responseCode === "00") {
        const data = getArrayFromResponse(response.data);

        if (typeof setPermissionKeys === "function") setPermissionKeys(data);
        if (typeof setFilteredPermissionKeys === "function") {
          setFilteredPermissionKeys(data);
        }

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage || "Failed to fetch permission keys"
        );
      }

      if (typeof setPermissionKeys === "function") setPermissionKeys([]);
      if (typeof setFilteredPermissionKeys === "function") {
        setFilteredPermissionKeys([]);
      }

      return [];
    } catch (error) {
      console.error("FetchPermissionKeys error:", error);
      toast.error("Error fetching permission keys");

      if (typeof setPermissionKeys === "function") setPermissionKeys([]);
      if (typeof setFilteredPermissionKeys === "function") {
        setFilteredPermissionKeys([]);
      }

      return [];
    } finally {
      setPermissionsLoading(false);
    }
  };

  // ================= GET ALL ROLES =================
  const FetchAllRoles = async (userId = "", setRoles, setFilteredRoles) => {
    setRolesLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/getAllRoles`, {
        userId: resolveUserId(userId),
      });

      if (response.data?.responseCode === "00") {
        const data = getArrayFromResponse(response.data);

        if (typeof setRoles === "function") setRoles(data);
        if (typeof setFilteredRoles === "function") setFilteredRoles(data);

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to fetch roles");
      }

      if (typeof setRoles === "function") setRoles([]);
      if (typeof setFilteredRoles === "function") setFilteredRoles([]);

      return [];
    } catch (error) {
      console.error("FetchAllRoles error:", error);
      toast.error("Error fetching roles");

      if (typeof setRoles === "function") setRoles([]);
      if (typeof setFilteredRoles === "function") setFilteredRoles([]);

      return [];
    } finally {
      setRolesLoading(false);
    }
  };

  // ================= CREATE ROLE =================
  // New API body:
  // {
  //   roleName: "Admin",
  //   accessManagement: [
  //     { permissionId: "2", menuId: "3", submenuId: "19" }
  //   ]
  // }
const InsertRole = async (
  { roleName, accessManagement = [], permissionIds = [] },
  onSuccess
) => {
  const cleanRoleName = roleName?.trim();

  if (!cleanRoleName) {
    toast.error("Role name is required");
    return false;
  }

  if (!Array.isArray(accessManagement) || accessManagement.length === 0) {
    toast.error("Please select at least one sidebar option");
    return false;
  }

  if (!Array.isArray(permissionIds) || permissionIds.length === 0) {
    toast.error("Please select at least one permission");
    return false;
  }

  const cleanAccessManagement = accessManagement
    .map((item) => ({
      menuId: String(item?.menuId || ""),
      submenuId: String(item?.submenuId || ""),
    }))
    .filter((item) => item.menuId && item.submenuId);

  const cleanPermissionIds = permissionIds
    .map((id) => String(id || ""))
    .filter(Boolean);

  if (cleanAccessManagement.length === 0) {
    toast.error("Please select sidebar menu and submenu properly");
    return false;
  }

  if (cleanPermissionIds.length === 0) {
    toast.error("Please select permissions properly");
    return false;
  }

  setCreateRoleLoading(true);

  try {
    const response = await api.post(`${ADMIN_BASE_URL}/insertRole`, {
      adminId: String(AdminId || ""),
      roleName: cleanRoleName,
      accessManagement: cleanAccessManagement,
      permissionId: cleanPermissionIds,
    });

    if (response.data?.responseCode === "00") {
      toast.success(response.data?.responseMessage || "Role created");

      if (typeof onSuccess === "function") {
        await onSuccess(response.data);
      }

      return true;
    }

    if (shouldShowLocalError(response.data)) {
      toast.error(response.data?.responseMessage || "Failed to create role");
    }

    return false;
  } catch (error) {
    console.error("InsertRole error:", error);
    toast.error("Error creating role");
    return false;
  } finally {
    setCreateRoleLoading(false);
  }
};


  // ================= GET ROLE PERMISSIONS =================
  const FetchRolePermissions = async (roleId, setRolePermissions) => {
    if (!roleId) {
      toast.error("Invalid role ID");
      return [];
    }

    setRolePermissionsLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/getRolePermissions`, {
        roleId: String(roleId),
      });

      if (response.data?.responseCode === "00") {
        const data = getArrayFromResponse(response.data);

        if (typeof setRolePermissions === "function") {
          setRolePermissions(data);
        }

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage || "Failed to fetch role permissions"
        );
      }

      if (typeof setRolePermissions === "function") {
        setRolePermissions([]);
      }

      return [];
    } catch (error) {
      console.error("FetchRolePermissions error:", error);
      toast.error("Error fetching role permissions");

      if (typeof setRolePermissions === "function") {
        setRolePermissions([]);
      }

      return [];
    } finally {
      setRolePermissionsLoading(false);
    }
  };

  // ================= UPDATE ROLE PERMISSION STATUS =================
  // Important: id = pk_permission_roles_id, not permission_id.
  const UpdateRolePermissionStatus = async (id, status = "Y", onSuccess) => {
    if (!id) {
      toast.error("Invalid role permission mapping ID");
      return false;
    }

    const cleanStatus = status === "Y" || status === true ? "Y" : "N";

    setUpdateLoading(true);

    try {
      const response = await api.post(
        `${ADMIN_BASE_URL}/updateRolePermissionStatus`,
        {
          id: String(id),
          status: cleanStatus,
        }
      );

      if (response.data?.responseCode === "00") {
        toast.success(response.data?.responseMessage || "Permission updated");

        if (typeof onSuccess === "function") {
          await onSuccess(response.data);
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage || "Failed to update permission"
        );
      }

      return false;
    } catch (error) {
      console.error("UpdateRolePermissionStatus error:", error);
      toast.error("Error updating permission");
      return false;
    } finally {
      setUpdateLoading(false);
    }
  };

  // ================= UPDATE ROLE STATUS =================
  const UpdateRoleStatus = async (id, status = "Y", onSuccess) => {
    if (!id) {
      toast.error("Invalid role ID");
      return false;
    }

    const cleanStatus = status === "Y" || status === true ? "Y" : "N";

    setUpdateLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/updateRoleStatus`, {
        id: String(id),
        status: cleanStatus,
      });

      if (response.data?.responseCode === "00") {
        toast.success(response.data?.responseMessage || "Role status updated");

        if (typeof onSuccess === "function") {
          await onSuccess(response.data);
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage || "Failed to update role status"
        );
      }

      return false;
    } catch (error) {
      console.error("UpdateRoleStatus error:", error);
      toast.error("Error updating role status");
      return false;
    } finally {
      setUpdateLoading(false);
    }
  };

  // ================= ASSIGN ROLE TO ADMIN =================
  const AssignRoleToAdmin = async (adminId, roleId, onSuccess) => {
    if (!adminId?.trim()) {
      toast.error("Admin ID is required");
      return false;
    }

    if (!roleId) {
      toast.error("Please select a role");
      return false;
    }

    setAssignRoleLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/assignRoleToAdmin`, {
        adminId: String(adminId).trim(),
        roleId: String(roleId),
      });

      if (response.data?.responseCode === "00") {
        toast.success(response.data?.responseMessage || "Role assigned");

        if (typeof onSuccess === "function") {
          await onSuccess(response.data);
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to assign role");
      }

      return false;
    } catch (error) {
      console.error("AssignRoleToAdmin error:", error);
      toast.error("Error assigning role");
      return false;
    } finally {
      setAssignRoleLoading(false);
    }
  };

  // ================= GET ALL ADMINS =================
  const FetchAllAdmins = async (userId = "", setAdmins, setFilteredAdmins) => {
    setAdminsLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/getAllAdmins`, {
        userId: resolveUserId(userId),
      });

      if (response.data?.responseCode === "00") {
        const data = getArrayFromResponse(response.data);

        if (typeof setAdmins === "function") setAdmins(data);
        if (typeof setFilteredAdmins === "function") setFilteredAdmins(data);

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to fetch admins");
      }

      if (typeof setAdmins === "function") setAdmins([]);
      if (typeof setFilteredAdmins === "function") setFilteredAdmins([]);

      return [];
    } catch (error) {
      console.error("FetchAllAdmins error:", error);
      toast.error("Error fetching admins");

      if (typeof setAdmins === "function") setAdmins([]);
      if (typeof setFilteredAdmins === "function") setFilteredAdmins([]);

      return [];
    } finally {
      setAdminsLoading(false);
    }
  };

  // ================= CREATE ADMIN =================
  const CreateAdmin = async (
    { firstName, lastName, userId, password, roleId },
    onSuccess
  ) => {
    const cleanFirstName = firstName?.trim();
    const cleanLastName = lastName?.trim();
    const cleanUserId = userId?.trim();
    const cleanPassword = password?.trim();

    if (!cleanFirstName) {
      toast.error("First name is required");
      return false;
    }

    if (!cleanLastName) {
      toast.error("Last name is required");
      return false;
    }

    if (!cleanUserId) {
      toast.error("User ID / Login ID is required");
      return false;
    }

    if (!cleanPassword) {
      toast.error("Password is required");
      return false;
    }

    if (!roleId) {
      toast.error("Please select a role");
      return false;
    }

    setCreateAdminLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/createAdmin`, {
        firstName: cleanFirstName,
        lastName: cleanLastName,
        userId: cleanUserId,
        password: cleanPassword,
        roleId: String(roleId),
      });

      if (response.data?.responseCode === "00") {
        toast.success(response.data?.responseMessage || "Admin created");

        if (typeof onSuccess === "function") {
          await onSuccess(response.data);
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to create admin");
      }

      return false;
    } catch (error) {
      console.error("CreateAdmin error:", error);
      toast.error("Error creating admin");
      return false;
    } finally {
      setCreateAdminLoading(false);
    }
  };

  // ================= GET USER ACCESS / SIDEBAR ACCESS =================
  // IMPORTANT:
  // userid = ""  => API returns all sidebar/menu access.
  // userid = "19" => API returns that user's assigned access.
  const FetchUserAccess = async (userid = "", setUserAccess) => {
    setUserAccessLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/getUserAccess`, {
        userid: String(userid || ""),
      });

      if (response.data?.responseCode === "00") {
        const data = getArrayFromResponse(response.data);

        if (typeof setUserAccess === "function") {
          setUserAccess(data);
        }

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to fetch access");
      }

      if (typeof setUserAccess === "function") setUserAccess([]);
      return [];
    } catch (error) {
      console.error("FetchUserAccess error:", error);
      toast.error("Error fetching user access");

      if (typeof setUserAccess === "function") setUserAccess([]);
      return [];
    } finally {
      setUserAccessLoading(false);
    }
  };

  // ================= UPDATE SIDEBAR STATUS =================
  const UpdateSideBarStatus = async (id, status = "Y", onSuccess) => {
    if (!id) {
      toast.error("Invalid sidebar access ID");
      return false;
    }

    const cleanStatus = status === "Y" || status === true ? "Y" : "N";

    setSideBarUpdateLoading(true);

    try {
      const response = await api.post(`${ADMIN_BASE_URL}/updateSideBarStatus`, {
        id: String(id),
        status: cleanStatus,
      });

      if (response.data?.responseCode === "00") {
        toast.success(response.data?.responseMessage || "Access updated");

        if (typeof onSuccess === "function") {
          await onSuccess(response.data);
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to update access");
      }

      return false;
    } catch (error) {
      console.error("UpdateSideBarStatus error:", error);
      toast.error("Error updating access");
      return false;
    } finally {
      setSideBarUpdateLoading(false);
    }
  };

  return {
    FetchPermissionKeys,
    FetchAllRoles,
    InsertRole,
    FetchRolePermissions,
    UpdateRolePermissionStatus,
    UpdateRoleStatus,
    AssignRoleToAdmin,
    FetchAllAdmins,
    CreateAdmin,
    FetchUserAccess,
    UpdateSideBarStatus,

    permissionsLoading,
    rolesLoading,
    rolePermissionsLoading,
    createRoleLoading,
    updateLoading,
    assignRoleLoading,
    adminsLoading,
    createAdminLoading,
    userAccessLoading,
    sideBarUpdateLoading,
  };
};

export default useAdmin;
